import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle, XCircle, Clock, Eye, Phone, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN, formatDate } from "../utils/format";

export default function Comprobantes() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [proof, setProof] = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/portal/admin/pending-proofs");
      setOrders(res.data);
    } catch { toast.error("Error al cargar comprobantes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openOrder = async (order) => {
    setSelected(order);
    setProof(null);
    setShowReject(false);
    setRejectReason("");
    if (order.payment_proof_id) {
      setLoadingProof(true);
      try {
        const res = await api.get(`/portal/admin/proof/${order.payment_proof_id}`);
        setProof(res.data);
      } catch { toast.error("No se pudo cargar el comprobante"); }
      finally { setLoadingProof(false); }
    }
  };

  const validate = async (action) => {
    setActing(true);
    try {
      const res = await api.post(`/portal/admin/orders/${selected.id}/validate`, {
        action,
        reason: action === "reject" ? rejectReason : undefined,
      });
      if (action === "approve") {
        toast.success(`✅ Pago validado — Posición en fila: #${res.data.queue_position}`);
      } else {
        toast.success("Comprobante rechazado. Se notificó al cliente.");
      }
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar");
    } finally {
      setActing(false);
    }
  };

  const openWhatsApp = (order) => {
    const phone = (order.customer_phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("El cliente no tiene teléfono registrado"); return; }
    const msg = encodeURIComponent(
      `Hola ${order.customer_name}, te confirmamos que tu pago del pedido ${order.folio} por $${order.total?.toFixed(2)} MXN fue recibido y está siendo validado. 🐾`
    );
    window.open(`https://web.whatsapp.com/send?phone=52${phone}&text=${msg}`, "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Comprobantes de Pago</h1>
          <p className="text-slate-500 text-sm mt-0.5">Portal de clientes — Pagos pendientes de validar</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-600 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
          <RefreshCw size={15} /> Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Cargando...</div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <CheckCircle size={40} className="mx-auto text-emerald-300 mb-3" />
          <p className="text-slate-500 font-medium">Sin comprobantes pendientes</p>
          <p className="text-slate-400 text-sm mt-1">Todos los pagos están validados</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {orders.map(o => (
            <div key={o.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:border-brand-200 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-900">{o.folio}</span>
                    <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">⏳ Pendiente validación</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">{o.customer_name}</div>
                  <div className="flex flex-wrap gap-x-4 mt-1 text-xs text-slate-400">
                    <span>Enviado: {formatDate(o.payment_proof_submitted_at)}</span>
                    <span>Método: {o.payment_method === "transferencia" ? "🏦 Transferencia" : "💳 MercadoPago"}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-brand-700 text-lg">{formatMXN(o.total)}</div>
                  <div className="flex gap-1 mt-2 justify-end">
                    <button onClick={() => openWhatsApp(o)} title="WhatsApp al cliente"
                      className="p-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-colors">
                      <Phone size={14} />
                    </button>
                    <button onClick={() => openOrder(o)}
                      className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                      <Eye size={13} /> Revisar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-brand-600 px-5 py-4 rounded-t-2xl">
              <div className="text-white font-bold">{selected.folio} — {selected.customer_name}</div>
              <div className="text-brand-200 text-sm">{formatMXN(selected.total)} · {selected.payment_method === "transferencia" ? "Transferencia" : "MercadoPago"}</div>
            </div>

            <div className="p-5 space-y-4">
              {/* Items summary */}
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Productos</div>
                {selected.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span>{item.product_name} ×{item.quantity} {item.unit}</span>
                    <span className="font-medium">{formatMXN(item.subtotal)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-brand-700 mt-2 pt-1 border-t border-slate-200">
                  <span>Total</span><span>{formatMXN(selected.total)}</span>
                </div>
              </div>

              {/* Proof image */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Comprobante de pago</div>
                {loadingProof ? (
                  <div className="flex items-center justify-center h-40 bg-slate-50 rounded-xl">
                    <span className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : proof ? (
                  <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                    {proof.content_type?.startsWith("image/") ? (
                      <img
                        src={`data:${proof.content_type};base64,${proof.data}`}
                        alt="Comprobante"
                        className="w-full object-contain max-h-64"
                      />
                    ) : (
                      <div className="p-6 text-center">
                        <div className="text-4xl mb-2">📄</div>
                        <div className="text-sm text-slate-600 font-medium">{proof.filename}</div>
                        <div className="text-xs text-slate-400 mt-1">{(proof.size / 1024).toFixed(1)} KB</div>
                        <a href={`data:${proof.content_type};base64,${proof.data}`} download={proof.filename}
                          className="mt-3 inline-flex items-center gap-1.5 text-brand-600 text-xs font-medium hover:underline">
                          <ExternalLink size={12} /> Descargar PDF
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-xl">Sin comprobante</div>
                )}
              </div>

              {/* Reject reason */}
              {showReject && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motivo del rechazo</label>
                  <input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="Ej: Imagen borrosa, monto incorrecto..."
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button onClick={() => setSelected(null)}
                  className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 py-2.5 rounded-xl text-sm font-medium transition-colors">
                  Cerrar
                </button>
                {!showReject ? (
                  <>
                    <button onClick={() => setShowReject(true)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 py-2.5 rounded-xl text-sm font-medium transition-colors border border-red-200">
                      <XCircle size={15} /> Rechazar
                    </button>
                    <button onClick={() => validate("approve")} disabled={acting}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
                      {acting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle size={15} />}
                      Validar pago
                    </button>
                  </>
                ) : (
                  <button onClick={() => validate("reject")} disabled={!rejectReason || acting}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
                    {acting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <XCircle size={15} />}
                    Confirmar rechazo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
